import {CompanyRoleAll} from '@/constants';
import {SupportedLanguage} from '@/types/common';
import {Company, ImprintConfig} from '@/types/company';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';

function convertToAdminCompanyForm(company: Company): AdminCompaniesFormValues {
  return {
    _id: company._id || '',
    title: company.title || '',
    email: company.email || '',
    logo: company.logo || '',
    imprint: company.imprint,
    welcome: company.welcome || '',
    roles: company.roles || [],
    labelLanguage: 'de',
    allowedSports: company.allowedSports || [],
    logoUploaded: false
  };
}

export interface AdminCompaniesFormValues {
  _id: string;
  title: string;
  email: string;
  logo: string;
  imprint: ImprintConfig;
  welcome: string;
  roles: CompanyRoleAll[];
  labelLanguage: SupportedLanguage;
  allowedSports?: string[];
  logoUploaded?: boolean;
}

interface AdminCompaniesState {
  adminCompanies: Company[];
  adminCompaniesCompanyCreateForm: AdminCompaniesFormValues;
  adminCompaniesCompanyEditForms: Record<string, AdminCompaniesFormValues>;
}

export const defaultCompanyInfo: AdminCompaniesFormValues = {
  _id: '',
  title: '',
  email: '',
  logo: '',
  imprint: {
    url: '',
    labels: {
      de: '',
      en: ''
    }
  },
  welcome: '',
  roles: [],
  labelLanguage: 'de',
  allowedSports: [],
  logoUploaded: false
};

export const initialState: AdminCompaniesState = {
  adminCompanies: [],
  adminCompaniesCompanyCreateForm: {
    ...defaultCompanyInfo
  },
  adminCompaniesCompanyEditForms: {}
};

const adminCompaniesFormSlice = createSlice({
  name: 'companyForm',
  initialState,
  reducers: {
    setCompaniesCreateFormData(
      state,
      action: PayloadAction<Partial<AdminCompaniesFormValues>>
    ) {
      state.adminCompaniesCompanyCreateForm = {
        ...state.adminCompaniesCompanyCreateForm,
        ...action.payload
      };
    },

    resetCompaniesCreateFormData(state) {
      state.adminCompaniesCompanyCreateForm = {...defaultCompanyInfo};
    },

    setCompaniesEditFormData(
      state,
      action: PayloadAction<{
        _id: string;
        formData?: Partial<AdminCompaniesFormValues>;
      }>
    ) {
      const {_id, formData} = action.payload;
      if (!formData) {
        state.adminCompaniesCompanyEditForms[_id] = {...defaultCompanyInfo};
      } else {
        state.adminCompaniesCompanyEditForms[_id] = {
          ...state.adminCompaniesCompanyEditForms[_id],
          ...formData
        };
      }
    },

    resetCompaniesEditFormDataForCompany(state, action: PayloadAction<string>) {
      const originalCompany = state.adminCompanies.find(
        (company) => company._id === action.payload
      );
      if (originalCompany) {
        state.adminCompaniesCompanyEditForms[action.payload] = {
          ...convertToAdminCompanyForm(originalCompany),
          labelLanguage: 'de'
        };
      }
    },

    setCompaniesInRedux(
      state,
      action: PayloadAction<AdminCompaniesFormValues[]>
    ) {
      state.adminCompanies = action.payload;
      action.payload.forEach((company) => {
        state.adminCompaniesCompanyEditForms[company._id] = company;
      });
    },

    addCompanyToRedux(state, action: PayloadAction<AdminCompaniesFormValues>) {
      state.adminCompanies.push(action.payload);
      state.adminCompanies.sort((a, b) => a.title.localeCompare(b.title));
      state.adminCompaniesCompanyEditForms[action.payload._id] = action.payload;
    },
    updateCompanyinRedux(
      state,
      action: PayloadAction<{company: AdminCompaniesFormValues}>
    ) {
      const {company} = action.payload;
      const index = state.adminCompanies.findIndex(
        (c) => c._id === company._id
      );

      if (index !== -1) {
        state.adminCompanies[index] = company;
      }
      state.adminCompaniesCompanyEditForms[company._id] = company;
    },

    removeCompanyFromRedux(state, action: PayloadAction<{companyId: string}>) {
      state.adminCompanies = state.adminCompanies.filter(
        (c) => c._id !== action.payload.companyId
      );
      delete state.adminCompaniesCompanyEditForms[action.payload.companyId];
    }
  }
});

export const {
  setCompaniesCreateFormData,
  resetCompaniesCreateFormData,
  addCompanyToRedux,
  updateCompanyinRedux,
  setCompaniesInRedux,
  removeCompanyFromRedux,
  setCompaniesEditFormData,
  resetCompaniesEditFormDataForCompany
} = adminCompaniesFormSlice.actions;
export default adminCompaniesFormSlice.reducer;
